import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, X } from "lucide-react";

// Shown over the homepage when the marketing unsubscribe redirects here with
// ?unsub=1. The unsubscribe itself already happened server-side; this is
// just the on-brand confirmation, and it auto-closes so the visitor is left on
// the live homepage (a real page view).
const AUTO_CLOSE_MS = 3000;

export default function UnsubscribedPopup() {
    const [params, setParams] = useSearchParams();
    const [open, setOpen] = useState(false);

    // Open when the flag is present, then immediately strip it from the URL so a
    // refresh or share doesn't re-trigger the popup.
    useEffect(() => {
        if (params.get("unsub") !== "1") return;
        setOpen(true);
        const next = new URLSearchParams(params);
        next.delete("unsub");
        setParams(next, { replace: true });
    }, [params, setParams]);

    const close = useCallback(() => setOpen(false), []);

    // Auto-close after the timer.
    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(close, AUTO_CLOSE_MS);
        return () => clearTimeout(t);
    }, [open, close]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
            data-testid="unsubscribed-popup"
            onClick={close}
        >
            <div
                className="relative bg-background border-2 border-pos w-full max-w-md p-10 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                >
                    <X size={18} />
                </button>
                <CheckCircle2 className="text-pos mx-auto" size={44} />
                <h1 className="font-serif text-3xl font-black mt-4">You've been unsubscribed</h1>
                <p className="mt-3 text-sm text-muted-foreground">
                    You will no longer receive these emails from us.
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    This message closes automatically…
                </p>
            </div>
        </div>
    );
}
